import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { PasswordStrength, evaluatePassword } from '../components/dashboard/PasswordStrength';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';

/**
 * Only ever send the customer to a path on this site.
 *
 * `?redirect=` is attacker-controllable and lands in `navigate()` straight after
 * a successful sign-in — the moment a link like
 * `nso-india.com/login?redirect=//somewhere.else` is most likely to be trusted.
 * The browser refuses a cross-origin history push, so this was a broken redirect
 * rather than an open one, but "the browser stops it" is not the guarantee to
 * rest on. A single leading slash, no scheme, no protocol-relative `//`.
 */
const safeRedirect = (value) =>
  typeof value === 'string' && /^\/(?!\/)/.test(value) ? value : null;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, googleLogin } = useAuth();

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sign-up asked for a password with no rules at all, while the reset and
    // Security Centre screens both demand eight characters and a mix. The weakest
    // door decides how strong the lock is, so it is the same gate here.
    if (!isLogin && !evaluatePassword(formData.password).isAcceptable) {
      toast.error('Use at least 8 characters with a mix of cases, numbers or symbols.');
      return;
    }

    setLoading(true);

    const searchParams = new URLSearchParams(window.location.search);
    const redirect = safeRedirect(searchParams.get('redirect'));

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success('Login successful!');

        if (redirect) {
          navigate(redirect);
          return;
        }

        // Redirect admin users to admin panel. The user object may be nested
        // under `data.user`, so normalize before reading the role.
        const loggedInUser = userData?.user || userData;
        if (loggedInUser?.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard/settings');
        }
      } else {
        const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Registration failed');
        }

        toast.success('Account created! Please login.');
        setIsLogin(true);
        setFormData({ ...formData, password: '' });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
            <img
              src="/logo.png"
              alt="Nso-India"
              className="h-10 w-auto object-contain"
            />
          </Link>

          <div className="bg-white rounded-3xl p-8 shadow-soft">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl text-secondary mb-2">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h1>
              <p className="text-muted-foreground font-body">
                {isLogin ? 'Sign in to your account to continue' : 'Join us for the best shopping experience'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="font-body">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required={!isLogin}
                      className="pl-10 rounded-xl h-12"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="font-body">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    className="pl-10 rounded-xl h-12"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="font-body">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="pl-10 pr-10 rounded-xl h-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Shown while signing up, so the rule the submit enforces is on
                  screen rather than arriving as a rejection. */}
              {!isLogin && formData.password && (
                <div className="rounded-2xl bg-surface border border-gray-100 p-4">
                  <PasswordStrength value={formData.password} />
                </div>
              )}

              {isLogin && (
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:underline font-body"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-body btn-press"
                data-testid="submit-btn"
              >
                {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  //console.log("Google Credential Received:", credentialResponse.credential?.substring(0, 10) + "...");
                  const searchParams = new URLSearchParams(window.location.search);
                  const redirect = safeRedirect(searchParams.get('redirect'));

                  try {
                    const userData = await googleLogin(credentialResponse.credential);
                    //console.log("Backend Google Login Success:", userData);
                    toast.success('Google Login successful!');

                    if (redirect) {
                      navigate(redirect);
                      return;
                    }

                    const gUser = userData?.user || userData;
                    if (gUser?.role === 'admin') {
                      navigate('/admin');
                    } else {
                      navigate('/dashboard/settings');
                    }
                  } catch (error) {
                    console.error("Google Login Backend Error:", error);
                    toast.error(error.message || 'Backend Google login failed');
                  }
                }}
                onError={() => {
                  toast.error('Google Login Failed');
                }}
                useOneTap
                theme="outline"
                shape="pill"
              />
            </div>

            <p className="text-center mt-6 text-sm text-muted-foreground font-body">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-1 text-primary hover:underline font-medium"
                data-testid="toggle-auth-btn"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=1200"
          alt="Shopping"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-heading text-4xl text-white mb-4">
            Premium Shopping Experience
          </h2>
          <p className="text-white/80 font-body text-lg">
            Discover the finest optical eyewear, latest smartphones, and thoughtful gift cards.
          </p>
        </div>
      </div>
    </div>
  );
}
